import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useRouter as useNavigationRouter } from 'next-nprogress-bar'
import { useEffect, useMemo, useState } from 'react'

import { useFormatPrice } from '@src/blocks/currency'
import { BUTTON_TYPES } from '@src/quince-components/src/atoms/button'
import { Checkbox } from '@src/quince-components/src/atoms/checkbox/Checkbox'
import { ProductDisplayButton } from '@src/quince-components/src/atoms/productCTA/product-display-button'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { ViewId } from '@src/quince-site-common/src/domains/ProductVariant/Customization'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { useCart } from '@src/quince-site-common/src/hooks/cart'
import { VARIANT_QUANTITY_MAP_QUERY_PARAM } from '@src/quince-site-common/src/organisms/b2b/self-serve/customizationPage/types'
import getB2BRoute from '@src/quince-site-common/src/utils/b2b/get-b2b-route'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import styles from './b2b-product-purchase-options.module.scss'

interface B2BProductPurchaseOptionsProps {
  product?: Product
  selectedOptions?: ProductVariantOption[]
  productPurchaseInfo?: IProductPurchaseInfo
}

export default function B2BProductPurchaseOptions({
  product,
  selectedOptions,
  productPurchaseInfo,
}: B2BProductPurchaseOptionsProps) {
  const { t } = useTranslation('common')
  const formatPrice = useFormatPrice()
  const {
    bulkOrderInputStateByVariantId,
    setBulkOrderInputStateByVariantId,
    storeProgramResponse,
    isCustomizationEnabled,
    setIsCustomizationEnabled,
  } = useStorefrontModeContext()
  const { addBulkOrderCustomizationToCart, isActionInProgress, errorMessage: addToCartErrorMessage } = useCart()
  const navigationRouter = useNavigationRouter()
  const [errorMessage, setErrorMessage] = useState<string>('')

  // Only positive quantities become line items / vq entries — 0-qty sizes are dropped.
  const getVariantQuantityMap = () => {
    return Object.entries(bulkOrderInputStateByVariantId ?? {}).reduce<Record<string, number>>(
      (acc, [variantId, state]) => {
        if (state.quantity > 0) acc[variantId] = state.quantity
        return acc
      },
      {},
    )
  }

  const areAllColorVariantsOutOfStock = useMemo(() => {
    if (!product || !productPurchaseInfo) return false

    const selectedColorOptions = (selectedOptions ?? []).filter(option => option.name === ProductOptionName.Color)
    const variantsForColor = productHelper.getVariantsForOptions(product, selectedColorOptions)

    if (variantsForColor.length === 0) return false

    return variantsForColor.every(variant => {
      const svDetails = productPurchaseInfo.svidVsSvDetails?.[variant.id]
      const variantSupportsCustomization = variant?.customization != undefined
      const available = isCustomizationEnabled
        ? variantSupportsCustomization
          ? (svDetails?.availableForCustomization ?? 0)
          : 0
        : (svDetails?.quantityAvailable ?? 0)
      return available <= 0
    })
  }, [product, selectedOptions, productPurchaseInfo, isCustomizationEnabled])

  const isValid = () => {
    if (areAllColorVariantsOutOfStock) return false

    const values = Object.values(bulkOrderInputStateByVariantId ?? {})

    if (values.length === 0) {
      setErrorMessage(t('please_enter_a_size_quantity'))
      return false
    }

    // Zero-qty sizes are silently dropped from the order (see getVariantQuantityMap). Proceed as long as
    // at least one size has a positive quantity; only error when nothing positive remains.
    if (!values.some(state => state.quantity > 0)) {
      setErrorMessage(t('please_enter_valid_quantity'))
      return false
    }

    // Block only on a real validation error for a size that is actually being ordered (qty > 0).
    if (values.some(state => state.quantity > 0 && state.errorMessage)) {
      setErrorMessage(t('update_size_qty_to_proceed'))
      return false
    }

    return true
  }

  const onAddToBag = async () => {
    if (!isValid() || !product) return

    setErrorMessage('')

    const variantQuantityMap = getVariantQuantityMap()

    const bulkLineItemDetails = Object.entries(variantQuantityMap).map(([variantId, quantity]) => ({
      variantId: Number(variantId),
      quantity: quantity,
    }))

    const success = await addBulkOrderCustomizationToCart?.({ product, bulkLineItemDetails })
    if (success) {
      setBulkOrderInputStateByVariantId?.({})
    }
  }

  const onCheckboxChange = (checked: boolean) => {
    setIsCustomizationEnabled?.(checked)

    trackEvent(TELEMETRY_EVENT_NAMES.CUSTOMIZATION_CHECKBOX_CLICK, {
      isSelected: checked,
      productId: product?.id,
    })
  }

  const handleStartCustomizing = (): void => {
    if (!isValid() || !product || !storeProgramResponse) return

    setErrorMessage('')

    const variantQuantityMap = getVariantQuantityMap()
    const vq = Object.entries(variantQuantityMap)
      .map(([variantId, quantity]) => `${variantId}:${quantity}`)
      .join(',')
    // Preserve existing PDP params (e.g. color) and add vq for both the PDP and customization URLs.
    const searchParams = new URLSearchParams(window.location.search)
    if (vq) searchParams.set(VARIANT_QUANTITY_MAP_QUERY_PARAM, vq)
    const queryString = searchParams.toString()

    // Stamp vq on the current PDP entry via history.replaceState (not router.replace): synchronous, no router re-render/cycle that would lag the push below; browser-back then restores the qty.
    const pdpUrlWithVq = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
    const currentHistoryState = window.history.state
    window.history.replaceState(
      currentHistoryState ? { ...currentHistoryState, as: pdpUrlWithVq } : currentHistoryState,
      '',
      pdpUrlWithVq,
    )

    const customizationUrl = getB2BRoute(
      `/${storeProgramResponse?.storeProgramDetails?.slug}/p/customization/${product.slug}?${queryString}`,
    )

    navigationRouter.push(customizationUrl)
  }

  // Reset error message when all sizes and quantities are valid
  useEffect(() => {
    if (Object.values(bulkOrderInputStateByVariantId ?? {}).every(state => !state.errorMessage)) {
      setErrorMessage('')
    }
  }, [bulkOrderInputStateByVariantId])

  const customizationPrice = useMemo(() => {
    if (!product) return undefined

    const selectedColorOptions = (selectedOptions ?? []).filter(option => option.name === ProductOptionName.Color)
    const variantsForSelectedColor = productHelper.getVariantsForOptions(product, selectedColorOptions)

    const customizationVariant = variantsForSelectedColor.find(variant => variant.customization !== undefined)
    const frontCustomizationPrintArea = customizationVariant?.customization?.printAreas.find(
      area => area.viewId === ViewId.FRONT,
    )
    return frontCustomizationPrintArea?.methods[0]?.applicationFee
  }, [product, selectedOptions])

  const selectedColorValue = useMemo(
    () => selectedOptions?.find(option => option.name === ProductOptionName.Color)?.value,
    [selectedOptions],
  )

  //By default customization is enabled if the customization is present in selected color variants
  useEffect(() => {
    if (customizationPrice != undefined) {
      setIsCustomizationEnabled?.(true)
    } else {
      setIsCustomizationEnabled?.(false)
    }
  }, [selectedColorValue, customizationPrice, setIsCustomizationEnabled])

  return (
    <div className={styles.container}>
      {customizationPrice != undefined && (
        <div className={styles.customizationWrapper}>
          <div className={styles.checkboxWrapper}>
            <Checkbox
              isChecked={isCustomizationEnabled}
              setIsChecked={onCheckboxChange}
              label={t('customization_label', { price: formatPrice(customizationPrice) })}
              className={styles.customizationCheckbox}
              labelClassName={styles.customizationCheckboxLabel}
            />
          </div>
        </div>
      )}

      <div>
        {errorMessage || addToCartErrorMessage ? (
          <div className={classNames(styles.message, styles.errorMessage)}>{errorMessage || addToCartErrorMessage}</div>
        ) : areAllColorVariantsOutOfStock ? (
          <div className={classNames(styles.message, styles.errorMessage)}>{t('out_of_stock')}</div>
        ) : (
          <div className={classNames(styles.message, styles.infoMessage)}>{t('in_stock_and_ready_to_ship')}</div>
        )}
        {isCustomizationEnabled ? (
          <ProductDisplayButton
            className={styles.actionButton}
            onClick={handleStartCustomizing}
            disabled={areAllColorVariantsOutOfStock}
          >
            {t('start_customizing')}
          </ProductDisplayButton>
        ) : (
          <ProductDisplayButton
            className={styles.actionButton}
            onClick={() => void onAddToBag()}
            buttonType={BUTTON_TYPES.SECONDARY}
            showLoader={isActionInProgress}
            disabled={areAllColorVariantsOutOfStock}
          >
            {t('add_to_bag')}
          </ProductDisplayButton>
        )}
      </div>
    </div>
  )
}
